import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Grid,
  Alert,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
  Modal,
  MobileStepper,
} from "@mui/material";
import { motion } from "framer-motion";

import { Check, Download, InfoRounded, KeyboardArrowLeft, KeyboardArrowRight, Upload } from "@mui/icons-material";
import { Await, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import getErrorMessage from "../../helper/getErrorMessage";
import ComponentBackdrop from "../../components/backdrop";
import { valueTagData } from "../../data/valueTag";
import { useApiClient } from '../../context/ApiClientContext';
import api from "../../helper/baseApi";
import dayjs from "dayjs";
import ExportEmployeeExcel from "../User/exportEmployeeExcel";
import ImportUser from "../../components/importUser";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadUserButton from "../User/uploadUserButton";

const steps = [
  "Account Setup",
  "A Little About You",
  "Your Value Tag",
  "Your Team Identity",
  "Employee Database",
  // "Completed",
];

export default function OnboardingStepper({ open, onClose, fetchData }) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const heardFrom = sessionStorage.getItem("source" || "");

  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;

  const [maxLimitReached, setMaxLimitReached] = useState(false);
  const [selectedTags, setSelectedTags] = useState(() => {
    const storedTags = sessionStorage.getItem("valuetags");
    return storedTags ? JSON.parse(storedTags) : [];
  });
  const [valueTags, setValueTags] = useState(() => {
    const savedAvailableTags = sessionStorage.getItem("availableTags");
    return savedAvailableTags ? JSON.parse(savedAvailableTags) : valueTagData;
  });
  const [lastSelectedTagId, setLastSelectedTagId] = useState(null);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedSource, setSelectedSource] = useState(() => {
    const storedSource = sessionStorage.getItem("source");
    return storedSource || "";
  });
  const [otherSourceText, setOtherSourceText] = useState("");
  const [poisumName, setPoisumName] = useState(() => {
    const storedName = sessionStorage.getItem("poisumName");
    return storedName || "";
  });
  const [selectedDOB, setSelectedDOB] = useState(null);
  const [selectedGender, setSelectedGender] = useState('');
  const [teamIdentity, setTeamIdentity] = useState();
  const [employeeDatabase, setEmployeeDatabase] = useState();
  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [importError, setImportError] = useState("");
  const [importData, setImportData] = useState([]);
  const [importJob, setImportJob] = useState([]);
  const [backendErrors, setBackendErrors] = useState([]);
  const [jobGradeUploaded, setJobGradeUploaded] = useState(false);

  const ValueTagQuota = sessionStorage.getItem("ValueTagQuota");

  const MAX_SELECTED_TAGS = ValueTagQuota ? parseInt(ValueTagQuota, 10) : 2;
  const isStep = (step) => {
    switch (step) {
      case 1:
        return selectedDOB !== null && selectedGender !== ''; // DOB is required
      case 2:
        return selectedTags.length > 0; // At least 1 tag required
      default:
        return true; // Other steps are optional
    }
  };

  const doesStepHaveData = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Welcome - always considered "done"
        return true;
      case 1: // DOB & Gender
        return !!selectedDOB;
      case 2: // Value Tags
        return selectedTags.length > 0;
      case 3: // Team Identity
        return !!poisumName;
      case 4: // Employee Database
        return !!employeeDatabase;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    // Step validation
    if (activeStep === 1 && !selectedDOB) {
      toast.error("Please select your date of birth");
      return;
    }

    if (activeStep === 2 && selectedTags.length === 0) {
      toast.error("Please select at least one value tag");
      return;
    }

    let response;

    try {
      switch (activeStep) {
        case 0:
          response = await api.get(`${mobileApiUrl}usage/check-usage`);
          sessionStorage.setItem("ValueTagQuota", response.data.valueTagQuota);
          break;

        case 1:
          response = await api.post(`${mobileApiUrl}onboarding/update-details`, {
            dateOfBirth: selectedDOB.format("YYYY-MM-DD"),
            gender: selectedGender
          });
          break;

        case 2:
          const valueTag = selectedTags.map(tag => tag.name);
          console.log("valueTag", valueTag);

          response = await api.post(`${mobileApiUrl}onboarding/add-value-tag`, {
            valueTagNames: valueTag
          });
          break;

        case 3:
          response = await api.put(`${mobileApiUrl}onboarding/update-email-greeting`, {
            emailGreeting: poisumName
          });
          localStorage.setItem("poisumName", poisumName);
          break;
      }
    } catch (error) {
      // If backend returns error response with message
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      return;
    }
    // ✅ Only go to next step if API call succeeded
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => setActiveStep(0);

  const handleSelectSource = (option) => {
    sessionStorage.setItem("source", JSON.stringify(option));

    setSelectedSource(option);
    if (option !== "Others") {
      setOtherSourceText("");
    }
  };

  const handleResetImport = () => {
    setImportError("");
    setBackendErrors([]);
    setImportData({});
    setFileName("");
  };

  const hasFile =
    importData &&
    Array.isArray(importData.employees) &&
    importData.employees.length > 0;


  useEffect(() => {
    setJobGradeUploaded(false);
  }, [importJob]);

  const handleFinish = async () => {
    // const hasFile =
    //   importData &&
    //   Array.isArray(importData.employees) &&
    //   importData.employees.length > 0;

    // // 🧭 If no file uploaded, skip API and move to next step
    // if (!hasFile) {
    setOpenBackdrop(true);
    setTimeout(() => {
      toast.success("Your Growth Journey Starts Here. Welcome to POISUM!");
      onClose();
      setOpenBackdrop(false);
      localStorage.setItem("isFirstTime", false);
      window.location.reload();
    }, 2000);
    sessionStorage.clear();
    return;
    // }

    // try {
    //   console.log("importJob", importJob);
    //   setOpenBackdrop(true);

    // ✅ Check if there is job grade data
    // const hasJobData =
    //   importJob && Array.isArray(importJob) && importJob.length > 0;

    // // STEP 1: Upload job grades ONLY if there is data AND not already uploaded
    // if (hasJobData && !jobGradeUploaded) {
    //   const responseJob = await api.post(
    //     `${mobileApiUrl}jobGrade/create-jobgrade-list`,
    //     { jobGradeList: importJob }
    //   );

    //   if (responseJob.status === 200) {
    //     console.log("✅ Job Grade upload success");
    //     setJobGradeUploaded(true); // mark as uploaded
    //   } else {
    //     // Job grade failed → stop, don’t call user API
    //     toast.error("Job Grade upload failed. Aborting user import.");
    //     setOpenBackdrop(false);
    //     return;
    //   }
    // } else if (!hasJobData) {
    //   console.log("⚡ No job grade data → skipping job grade upload");
    // } else {
    //   console.log("⚡ Job grade already uploaded → skipping upload");
    // }

    // // STEP 2: Upload users
    // const response = await api.post(`${mobileApiUrl}user/add-excel-user`, {
    //   employees: importData.employees,
    // });

    // if (response.data.success === false) {
    //   const mainMessage =
    //     response.data.message || "Upload failed due to invalid data.";
    //   const rowErrors = response.data.data?.errors || [];

    //   setBackendErrors(rowErrors);
    //   toast.error(mainMessage);
    //   setOpenBackdrop(false);
    //   return;
    // }

    // ✅ All success
    // setBackendErrors([]);
    // setTimeout(() => {
    //   fetchData();
    //   toast.success("Your Growth Journey Starts Here. Welcome to POISUM!");
    //   onClose();
    //   setOpenBackdrop(false);
    //   localStorage.setItem("isFirstTime", false);
    // }, 3000);

    // } catch (error) {
    //   console.error("❌ Submission error:", error);
    //   const apiData = error.response?.data;
    //   const rowErrors = apiData?.data?.errors || [];
    //   const mainMessage =
    //     apiData?.message || "Something went wrong, please try again.";

    //   if (rowErrors.length > 0) setBackendErrors(rowErrors);
    //   toast.error(mainMessage);

    // } finally {
    // sessionStorage.clear();
    // }
  };

  const handleSubmitFinal = async () => {
    setOpenBackdrop(true);
    try {
      const payload = {
        email: sessionStorage.getItem("email" || ""),
        firstname: sessionStorage.getItem("firstname" || ""),
        lastname: sessionStorage.getItem("lastname" || ""),
        countryId: parseInt(sessionStorage.getItem("country"), 10),
        companyName: sessionStorage.getItem("company" || ""),
        valueTagId: selectedTags,
        password: sessionStorage.getItem("password"),
        whereKnowPoisum: heardFrom,
        poisumName: poisumName,
      };

      console.log("payload", payload);
    } catch (error) {
      console.log("error1", error);
      toast.error(getErrorMessage(error));
    } finally {
      setOpenBackdrop(false);
    }
  };

  const toggleTag = (tag) => {
    const isSelected = selectedTags.some(
      (t) => t.valueTagId === tag.valueTagId
    );
    const updatedTags = isSelected
      ? selectedTags.filter((t) => t.valueTagId !== tag.valueTagId)
      : [...selectedTags, tag];

    setSelectedTags(updatedTags);
    sessionStorage.setItem("valuetags", JSON.stringify(updatedTags));
  };

  const handleMouseEnter = (tag) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);

    const timeout = setTimeout(() => {
      showTagDescription(tag);
    }, 100);

    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setLastSelectedTagId(null);
  };


  const handleClick = (tag) => {
    toggleTagSelection(tag);
  };

  const showTagDescription = (tag) => {
    setLastSelectedTagId(tag.valueTagId);
  };

  // Extracted tag selection logic
  const toggleTagSelection = (tag) => {
    const isCurrentlySelected = selectedTags.some(
      (t) => t.valueTagId === tag.valueTagId
    );

    if (isCurrentlySelected) {
      deselectTag(tag);
    } else {
      selectTag(tag);
    }
  };

  const deselectTag = (tag) => {
    toggleTag(tag);

    setMaxLimitReached(false);
  };

  const selectTag = (tag) => {
    if (selectedTags.length >= MAX_SELECTED_TAGS) {
      handleMaxLimitReached();
      return;
    }

    toggleTag(tag);
    setLastSelectedTagId(tag.valueTagId);
    setMaxLimitReached(selectedTags.length + 1 === MAX_SELECTED_TAGS);
  };

  const handleMaxLimitReached = () => {
    setMaxLimitReached(true);
    toast.error(`Maximum limit of ${ValueTagQuota || 2} tags has been reached`, {
      autoClose: 1000,
      position: "top-center"
    });
  };

  const addCustomTag = () => {
    if (!otherValue.trim()) return;

    if (selectedTags.length >= MAX_SELECTED_TAGS) {
      toast.error("Max limit reached");
      return;
    }

    const newTag = {
      valueTagId: `custom-${Date.now()}`,
      name: otherValue.trim(),
      description: otherValue.trim(),
      isCustom: true,
      iconCode: "arm-flex",
    };

    const updateAvailableTag = [...valueTags, newTag];
    setValueTags(updateAvailableTag);
    sessionStorage.setItem("availableTags", JSON.stringify(updateAvailableTag));

    //update selected tags
    const updatedTags = [...selectedTags, newTag];
    setSelectedTags(updatedTags);
    sessionStorage.setItem("valuetags", JSON.stringify(updatedTags));

    setOtherValue("");
    setLastSelectedTagId(newTag.valueTagId);
    setMaxLimitReached(updatedTags === MAX_SELECTED_TAGS);
    // setShowOtherInput(false);
  };

  const CustomStepIcon = (props) => {
    const { active, completed, className, icon, hasData } = props;

    return (
      <Box
        className={className}
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: active || completed ? "primary.main" : "secondary.main",
          color: active || completed ? "secondary.main" : "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
          fontSize: "14px",
        }}
      >
        {hasData ? <Check /> : icon}
      </Box>
    );
  };

  const InfoRow = ({ label, value, isMobile, labelWidth }) => (
    <Box
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      alignItems={isMobile ? "flex-start" : "center"}
      mb={1}
    >
      <Typography
        variant="body1"
        sx={{
          minWidth: isMobile ? "auto" : labelWidth,
          fontWeight: "600",
          color: "text.primary",
        }}
      >
        {label}:
      </Typography>
      <Typography variant="" sx={{ color: "text.secondary" }}>
        {value || "-"}
      </Typography>
    </Box>
  );

  // const heardFromWidget = () => {
  //   return (
  //     <Box p={4} textAlign={"center"}>
  //       <Box mb={3}></Box>

  //       <Grid container spacing={2} justifyContent="center">
  //         {hearOptions.map((option) => (
  //           <Grid item xs={6} sm={4} key={option}>
  //             <Box
  //               onClick={() => handleSelectSource(option)}
  //               sx={{
  //                 px: 2,
  //                 py: 2,
  //                 borderRadius: 2,
  //                 border: "2px solid",
  //                 borderColor:
  //                   selectedSource === option ? "primary.main" : "divider",
  //                 bgcolor:
  //                   selectedSource === option
  //                     ? "tertiary.main"
  //                     : "transparent",
  //                 color:
  //                   selectedSource === option ? "white" : "text.primary",
  //                 textAlign: "center",
  //                 cursor: "pointer",
  //                 transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  //                 transform: "scale(1)",
  //                 boxShadow: "none",
  //                 "&:hover": {
  //                   transform: "scale(1.03)",
  //                   boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  //                   borderColor: "primary.main",
  //                   bgcolor:
  //                     selectedSource === option
  //                       ? "tertiary.main"
  //                       : "action.hover",
  //                 },
  //                 "&:active": {
  //                   transform: "scale(0.98)",
  //                 },
  //               }}
  //             >
  //               <Typography variant="body1" fontWeight={500}>
  //                 {option}
  //               </Typography>
  //             </Box>
  //           </Grid>
  //         ))}
  //       </Grid>

  //       {selectedSource === "Others" && (
  //         <Box mt={3}>
  //           <TextField
  //             fullWidth
  //             size="medium"
  //             label="Tell us where you heard about us"
  //             variant="outlined"
  //             value={otherSourceText}
  //             onChange={(e) => setOtherSourceText(e.target.value)}
  //             sx={{
  //               "& .MuiOutlinedInput-root": {
  //                 borderRadius: 2,
  //               },
  //             }}
  //           />
  //         </Box>
  //       )}
  //     </Box>
  //   );
  // }

  const handleHoverTag = (tag) => {
    const prevSelected = selectedTags.some(
      (t) => t.valueTagId === tag.valueTagId

    );
    if (prevSelected) {
      toggleTag(tag);
      setLastSelectedTagId(null);
    } else {
      toggleTag(tag);
      setLastSelectedTagId(tag.valueTagId);
    }
  }

  // const lastSelectedTagDescription = lastSelectedTagId
  //   ? valueTags.find((tag) => tag.valueTagId === lastSelectedTagId)?.description
  //   : null;

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              px: { xs: 2, sm: 4 },
              pt: { xs: 1, sm: 2, md: 4 },
              flexShrink: 0,
            }}
          >
            <Typography variant={isMobile ? "subtitle" : "title"} sx={{ fontWeight: '700' }} gutterBottom>
              Start Your Journey with POISUM
            </Typography>
            <Typography variant={isMobile ? "body" : "subtitle"} sx={{ maxWidth: 1000 }}>
              Welcome onboard! You're just a few steps away from transforming the
              way your team stays aligned, engaged, and motivated. Let's get
              your POISUM space set up!
            </Typography>
          </Box>
        );
      case 1:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              px: { xs: 2, sm: 4 },
              pt: { xs: 2, sm: 4 },
              flexShrink: 0,
            }}
          >
            {/* Title */}
            <Typography variant={isMobile ? "subtitle" : "title"} sx={{ fontWeight: '700' }} gutterBottom>
              A Little About You
            </Typography>
            <Typography
              variant={isMobile ? "body" : "subtitle"}
              sx={{
                maxWidth: 1000,
                mb: 3,
                px: { xs: 1, sm: 0 }, // add horizontal padding on small screens
              }}
            >
              Let's add a splash of personality! It keeps your POISUM Profile complete
              and adds a friendly touch to your space.
            </Typography>

            {/* Form Grid */}
            <Grid
              container
              spacing={{ xs: 1, sm: 6, md: 10 }} // responsive spacing
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Gender */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    // gap: 1,
                    // mb: 2,
                  }}
                >
                  <Typography variant="body"
                    sx={{
                      minWidth: { xs: 80, sm: 100, md: 130 },
                      alignSelf: { xs: "start", sm: 'center' },
                      textAlign: { xs: "start", sm: 'center' },
                    }}
                  >
                    Gender  <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="large"
                      variant={selectedGender === "Male" ? "contained" : "outlined"}
                      onClick={() => setSelectedGender("Male")}
                      sx={{ fontSize: 14 }}
                    >
                      Male
                    </Button>
                    <Button
                      size="large"
                      variant={selectedGender === "Female" ? "contained" : "outlined"}
                      onClick={() => setSelectedGender("Female")}
                      sx={{ fontSize: 14 }}

                    >
                      Female
                    </Button>
                  </Stack>
                </Box>
                {/* <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Typography variant="body1">Gender:</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="large"
                      variant={selectedGender === "Male" ? "contained" : "outlined"}
                      onClick={() => setSelectedGender("Male")}
                    >
                      Male
                    </Button>
                    <Button
                      size="large"
                      variant={selectedGender === "Female" ? "contained" : "outlined"}
                      onClick={() => setSelectedGender("Female")}
                    >
                      Female
                    </Button>
                  </Stack>
                </Box> */}
              </Grid>

              {/* DOB */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Typography
                    variant="body"
                    sx={{
                      minWidth: { xs: 80, sm: 100, md: 130 },
                      alignSelf: { xs: "start", sm: 'center' },
                      textAlign: { xs: "start", sm: 'center' },
                    }}
                  >
                    DOB <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disableFuture
                      value={selectedDOB}
                      maxDate={dayjs("2010-01-01")}
                      slotProps={{
                        textField: {
                          size: "medium", fullWidth: true, sx: {
                            "& .MuiPickersSectionList-sectionContent": {
                              fontSize: "14px",
                              lineHeight: "1.2",
                            },
                          },
                        }, required: true
                      }}
                      onChange={(newValue) => setSelectedDOB(newValue)}

                    />
                  </LocalizationProvider>
                </Box>

                {/* <Box
                display="flex"
                alignItems="center"
                justifyContent={{ xs: "center", sm: "flex-start" }}
                flexDirection={{ xs: "column", sm: "row" }}
                gap={2}
              >
                <Typography variant="body1">DOB:</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disableFuture
                    slotProps={{ textField: { size: "medium", fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Box> */}
              </Grid>
            </Grid>
          </Box>

        );
      case 2:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant={isMobile ? "subtitle" : "title"} sx={{ fontWeight: '700' }} gutterBottom>
                Your Value Tags
              </Typography>
              <p />
              <Typography
                variant={isMobile ? "body" : "subtitle"}
                sx={{
                  maxWidth: 1000,
                  color: 'text.secondary'
                }}
              >
                Define the key values or behaviors you want to reward. These{" "}
                <strong>Value Tags</strong> help guide missions and align your
                team with what truly matters in your organization. Select up to <strong>{ValueTagQuota || 2} Value Tags</strong> that resonate most with your team's culture and goals.
              </Typography>
            </Box>

            <Box
              sx={{
                minHeight: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: 1200,
                mb: 1
              }}
            >
              <Box
                // border={1} 
                // borderColor={ 'divider'} 
                borderRadius={2}
                // p={2} 
                sx={{
                  width: '100%',
                  // bgcolor: 'background.paper'
                }}
              >
                {/* <Typography 
        variant="body1" 
        sx={{ 
          mb: 1, 
          color: 'text.primary',
          textAlign: 'center'
        }}
      >
        {`Select up to ${ValueTagQuota || 2} Value Tags that resonate most with your team's culture and goals.`}
      </Typography> */}



                {lastSelectedTagId && (
                  <Stack
                    direction="row"
                    // gap={1} 
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%" }}
                  >
                    <InfoRounded color="primary" />
                    <Typography variant={isMobile ? "body" : "subtitle"} sx={{ textAlign: 'center' }}>
                      &nbsp;<strong> {valueTags.find(tag => tag.valueTagId === lastSelectedTagId)?.description}</strong>
                    </Typography>
                  </Stack>
                )}

                {/* Empty state to maintain consistent height when no description */}
                {!lastSelectedTagId && (
                  <Box sx={{ height: 4 }} />
                )}
              </Box>
            </Box>

            <Grid
              container
              spacing={2}
              // mt={1}
              justifyContent="center"
              sx={{ maxWidth: 1200 }}
            >
              {valueTags.map((tag) => {
                const isSelected = selectedTags.some(
                  (t) => t.valueTagId === tag.valueTagId
                );
                return (
                  <Grid item xs={6} sm={4} key={tag.valueTagId}>
                    <Box
                      onClick={() => handleClick(tag)}
                      onMouseEnter={() => handleMouseEnter(tag)}
                      onMouseLeave={handleMouseLeave}
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 2,
                        border: "2px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        bgcolor: isSelected ? "primary.main" : "transparent",
                        color: isSelected ? "white" : "text.primary",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: "scale(1)",
                        boxShadow: "none",
                        "&:hover": {
                          transform: "scale(1.03)",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          borderColor: "primary.main",
                          bgcolor: isSelected
                            ? "primary.dark"
                            : "action.hover",
                        },
                        "&:active": {
                          transform: "scale(0.98)",
                        },
                      }}
                    >
                      <Typography
                        variant={isMobile ? "body" : "subtitle"}
                        fontWeight={500}
                        sx={{
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: isSelected ? "white" : "primary.main",
                          },
                        }}
                      >
                        {tag.name}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}

              <Grid item xs={6} sm={4}>
                <Box
                  onClick={() => setShowOtherInput(!showOtherInput)}
                  sx={{
                    px: 1,
                    py: 1,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: showOtherInput ? "primary.main" : "divider",
                    bgcolor: showOtherInput ? "tertiary.main" : "transparent",
                    color: showOtherInput ? "white" : "text.primary",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: "scale(1)",
                    boxShadow: "none",
                    "&:hover": {
                      transform: "scale(1.03)",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      borderColor: "primary.main",
                      bgcolor: showOtherInput
                        ? "tertiary.main"
                        : "action.hover",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  <Typography variant={isMobile ? "body" : "subtitle"} fontWeight={500}>
                    Others
                  </Typography>
                </Box>
              </Grid>

              {showOtherInput && (
                <Box
                  sx={{
                    width: "100%",
                    // maxWidth: 600,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Add custom value"
                      value={otherValue}
                      onChange={(e) => setOtherValue(e.target.value)}
                      inputProps={{ maxLength: 15 }}
                      sx={{
                        flexGrow: 1,
                        "& .MuiOutlinedInput-root": {
                          height: 56,
                        },
                      }}
                    />
                    <Button
                      disabled={selectedTags.length >= MAX_SELECTED_TAGS}
                      variant="contained"
                      onClick={addCustomTag}
                      sx={{
                        height: 56,
                        minWidth: 120,
                        fontSize: "14px",
                        backgroundColor: selectedTags.length >= MAX_SELECTED_TAGS ? 'grey' : 'secondary.main',
                        textTransform: "none",
                        //   "&:hover": {
                        //     backgroundColor: selectedTags.length >= MAX_SELECTED_TAGS ? 'grey' : 'primary.dark',
                        //   }, 
                      }}
                    >
                      ADD
                    </Button>
                  </Box>
                </Box>
              )}
            </Grid>

            <Alert
              severity="info"
              sx={{
                mt: 2,
                borderRadius: 2,
                textAlign: "left",
                bgcolor: "primary.main",
                color: "white",
              }}
            >
              <Typography variant="body">
                Want to add more value tags?{" "}
                <Link
                  href="https://www.poisum.com/pricing"
                  target="_blank"
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  Upgrade to Poisum Premium
                </Link>{" "}
                for unlimited options.
              </Typography>
            </Alert>
          </Box>
        );
      case 3:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant={isMobile ? "subtitle" : "title"} sx={{ fontWeight: '700', mb: 2 }} gutterBottom>
                  Your Team Identity
                </Typography>
                <p />
                <Typography
                  variant={isMobile ? "body" : "subtitle"}
                  sx={{
                    width: "100%",
                    maxWidth: 1100,
                    mb: "3",
                  }}
                >
                  What would you like your team to be called within POISUM?
                  Customize your user label to match your team's vibe - whether
                  it's "Champions," "Warriors," "Googlers," or something unique
                  to your company culture.
                </Typography>
              </Box>

              <TextField
                fullWidth
                size="medium"
                variant="outlined"
                placeholder="Enter your display name"
                value={poisumName}
                onChange={(e) => setPoisumName(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                  mt: 2,
                }}
                slotProps={{
                  inputProps: { maxLength: 50 },
                }}
              />

              <Typography
                variant="note"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  textAlign: "right",
                }}
              >
                {poisumName.length}/50 characters
              </Typography>
            </Stack>
          </Box>
        );
      case 4:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 3,
              // border: "1px dashed",
              borderColor: "grey.400",
              borderRadius: 2,
              backgroundColor: "background.main",
              textAlign: "center",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant={isMobile ? "subtitle" : "title"} sx={{ fontWeight: '700' }} gutterBottom>
                Employee Database
              </Typography><p />
              <Typography variant={isMobile ? "body" : "subtitle"} sx={{ maxWidth: 1000 }}>
                Let's bring your team onboard! Download the employee template, fill it up with your team
                details, and upload it back to get everyone ready to receive
                missions and rewards.
              </Typography>
            </Box>

            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={12}>
                <ExportEmployeeExcel />
              </Grid>

              <Grid item xs={12}>
                {/* <ImportUser
                  onValidData={setImportData}
                  onJobData={setImportJob}
                  onError={setImportError}
                  onFileName={setFileName}
                  onReset={handleResetImport} // 🧩 added this
                /> */}
                <UploadUserButton flag={'onboarding'} openBackdropOnboarding={setOpenBackdrop} />
              </Grid>
            </Grid>

            {/* 🟢 File name + error message displayed together here */}
            {/* <Box sx={{ mt: 2 }}>
              {fileName && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <Typography>
                    <strong>Selected file:</strong> {fileName}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleResetImport}
                    sx={{ minWidth: 0, p: 0.5 }}
                    aria-label="Remove file"
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              )}
              {importError && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {importError}
                </Typography>
              )}
              {importData?.employees?.length > 0 && (
                <Typography color="primary.main" sx={{ mt: 1 }}>
                  ✅ {importData.employees.length} employee records ready for submission.
                </Typography>
              )}
              {backendErrors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography color="error" sx={{ fontWeight: "bold" }}>
                    Upload failed. Please fix the following issues:
                  </Typography>
                  {backendErrors.map((err, index) => (
                    <Typography key={index} color="error" sx={{ ml: 2 }}>
                      Row {err.no + 1}: {err.errorMsg}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box> */}

            <Typography variant="note" color="text.secondary" sx={{ mt: 0.5 }}>
              Supported format: .xlsx, .xls, .csv (Name, Email columns required)
            </Typography>
          </Box>
        );
      // case 5:
      //   return (
      //     <Box p={4}>
      //       <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
      //         Your Growth Journey Starts Here <br />
      //         {/* You have Complete the Setup ! */}
      //       </Typography>

      //       <Typography variant="body1" color="text.secondary" mb={3}>
      //         Welcome to POISUM! <br />
      //       </Typography>
      //     </Box>
      //   );
      default:
        return "Unknown step";
    }
  };

  // useEffect(() => {
  //   console.log("active", activeStep);

  //   if (activeStep === 5) {
  //     setOpenBackdrop(true);

  //     toast.success("Your Growth Journey Starts Here. Welcome to POISUM!");

  //     const timer = setTimeout(() => {
  //       setOpenBackdrop(false);
  //       onClose();
  //     }, 3000); // 3 seconds

  //     return () => clearTimeout(timer);
  //   }
  // }, [activeStep]);

  const renderNextButton = (isMobile) => {
    const isLast = activeStep === steps.length - 1;

    // Disable Finish ONLY if there are visible errors
    const disableFinish =
      !!importError || backendErrors.length > 0;

    if (isMobile) {
      return isLast ? (
        <Button size="small" onClick={handleFinish} disabled={disableFinish}>
          Finish
        </Button>
      ) : (
        <Button size="small" onClick={handleNext} disabled={!isStep(activeStep)}>
          Next
          <KeyboardArrowRight />
        </Button>
      );
    }

    return isLast ? (
      <Button variant="contained" onClick={handleFinish} disabled={disableFinish}>
        Finish
      </Button>
    ) : (
      <Button
        disabled={!isStep(activeStep)}
        variant="contained"
        onClick={handleNext}
        sx={{ fontWeight: "bold" }}
      >
        Next
      </Button>
    );
  };

  return (
    <>
      <ComponentBackdrop openBackdrop={openBackdrop} />
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          backdropFilter: "blur(1px)",
          backgroundColor: "rgba(0, 0, 0, 0.15)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            px: { xs: 2, md: 18 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{ width: "100%" }}
          >
            <Box
              className="parent-modal"
              sx={{
                bgcolor: "background.main",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                borderRadius: 3,

                //   width: { xs: "100%", sm: 600, md: 1000, lg: 1200 }, // More responsive breakpoints
                //   maxWidth: "95vw", // Prevent touching screen edges
                height: {
                  xs: "auto", // Auto height on mobile
                  sm: "90vh",
                },
                maxHeight: { xs: "90vh" }, // Limit height on mobile
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignContent: "center",
                my: { xs: "auto", sm: 0 }, // Center vertically on mobile
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: { xs: 2, sm: 4 },
                  pt: { xs: 2, sm: 4 },
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    typography: {
                      xs: "subtitle",
                      md: "title",
                    },
                    textTransform: "uppercase",
                    fontWeight: "bold!important",
                    letterSpacing: {
                      xs: "0.5px",
                      md: "1px",
                    },
                    color: "text.primary",
                  }}
                >
                  Step Guide
                </Typography>
              </Box>

              {/* Stepper */}
              {!isMobile && (
                <Box
                  sx={{
                    my: { xs: 1, sm: 4, md: 8 },
                    position: "sticky",
                    top: 0,
                    bgcolor: "background.main",
                    zIndex: 1,
                    px: { xs: 2, sm: 4 },
                    flexShrink: 0,
                    overflow: "scroll",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >

                  <Stepper activeStep={activeStep}>
                    {steps.map((label, index) => (
                      <Step key={label}>
                        <StepLabel
                          StepIconComponent={(props) => (
                            <CustomStepIcon
                              {...props}
                              hasData={doesStepHaveData(index)}
                            />
                          )}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  px: { xs: 2, sm: 4 },
                  pb: 4,
                  scrollbarWidth: "none"
                }}
              >
                {getStepContent(activeStep)}
              </Box>

              {/* Footer (Desktop only) */}
              {!isMobile && (
                <Box
                  sx={{
                    flexShrink: 0,
                    bgcolor: "background.main",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    px: { xs: 2, sm: 4 },
                    py: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {/* {activeStep !== 0 && (
                  <Button variant="outlined" onClick={handleBack} sx={{ mr: 2 }}>
                    Back
                  </Button>
                )} */}
                  {renderNextButton(false)}
                </Box>
              )}

              {/* MobileStepper (Mobile only) */}
              {isMobile && (
                <Box sx={{ backgroundColor: "background" }}>
                  <MobileStepper
                    variant="text"
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    backButton={
                      <Button size="small" onClick={handleBack} disabled={true}>
                        <KeyboardArrowLeft />
                        Back
                      </Button>
                    }
                    nextButton={renderNextButton(true)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      "& .MuiMobileStepper-progress": { display: "none" }, // hide dots
                    }}
                    LinearProgressComponent={() => null}
                  >
                    <Typography variant="note" sx={{ flexGrow: 1, textAlign: "center" }}>
                      Step {activeStep + 1} of {maxSteps}
                    </Typography>
                  </MobileStepper>
                </Box>
              )}
            </Box>
          </motion.div>
        </Box>
      </Modal>
    </>
  );
}
